import {
  Breadcrumbs,
  Card,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
} from '@mui/joy';
import { AxiosError } from 'axios';
import { ClientResponseDTO, QueryResponse } from '@/types';
import { CreateEditClientModal } from '@/components/Pages/Clients/CreateEditClientModal';
import { createFileRoute, Link } from '@tanstack/react-router';
import { DeleteClientModal } from '@/components/Pages/Clients/DeleteClientModal';
import { SellTable } from '@/components/Pages/Sells/SellTable';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/axios';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/clients/$id')({
  component: ClientDetail,
});

function ClientDetail() {
  const { id } = Route.useParams();
  const { isAdmin, isSuperAdmin } = useAuth();
  const showActions = isAdmin || isSuperAdmin;

  const { data: client, isLoading } = useQuery<
    QueryResponse<ClientResponseDTO>,
    AxiosError,
    ClientResponseDTO
  >({
    queryKey: ['clients', id],
    queryFn: () => api.get(`/client/${id}`),
    select: ({ data }) => data.data,
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return <div>Cliente no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/clients'>Clientes</Link>
          <Typography>{client?.name}</Typography>
        </Breadcrumbs>

        {showActions && (
          <Stack direction='row' gap={1}>
            <CreateEditClientModal client={client} />
            <DeleteClientModal id={client?.id.toString()} name={client?.name} />
          </Stack>
        )}
      </Stack>

      <Card>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid xs={12} md={6}>
            <FormLabel>Nombre</FormLabel>
            <Input
              value={client?.name}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Email</FormLabel>
            <Input
              value={client?.email}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Teléfono</FormLabel>
            <Input
              value={client?.phoneNumber}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Dirección</FormLabel>
            <Input
              value={client?.address}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Zona</FormLabel>
            <Input
              value={client?.zoneName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Ciudad</FormLabel>
            <Input
              value={client?.cityName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ mt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography level='h4'>Ventas del cliente</Typography>
        </Box>

        <SellTable
          clientId={client.id}
          showClientColumn={false}
          showFilters={false}
          emptyMessage='No hay ventas para este cliente'
          errorTitle='Error al cargar las ventas del cliente'
        />
      </Card>
    </Box>
  );
}
